import React, { useEffect, useState, useCallback, useMemo } from "react";
import clsx from "clsx";
import moment from "moment";
import { formatPhoneNumber } from "react-phone-number-input";
import { useQueryClient } from "react-query";
import { connect, useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { Skeleton, HasPermissions, Button, OnlineLink } from "components";
import { ACHProcessorData } from "globalConstants";
import {
	CreditDebitByValueTypeCart,
	getAddress,
	getFullName,
	RefundPolicyByValue,
	viewSensitiveDataPermissionSet,
} from "helpers";
import {
	useCheckIfDocumentExistsMutation,
	useMCCFieldMutation,
	useProcessorFieldMutation,
	useProcessorsQuery,
	useSponsorFieldMutation,
	useSponsorsQuery,
	useTaxIdFieldMutation,
} from "queries";
import { merchantInfoActions, uiActions } from "store";
import { AchProcessorTypes, TypeOfBusinessValues } from "types";
import { taxIdNormalizer } from "validators";

import { AddressFieldRow } from "./addressFieldRow";
import EditInformationField from "./editInformationField";
import { FieldRow } from "./fieldRow";
import MerchantDescriptionForm from "./merchantDescriptionForm";
import TaxIdField from "./taxIdField";

import ShowIcon from "static/images/showFile.png";
import styles from "../../merchantInfo.module.scss";

const MerchantInfo = ({
	isLoading,
	merchantData,
	getMerchantInformation,
	isEditable = false,
	isProcessorEditable = false,
	isSponsorEditable = false,
	refetchBankNotes = undefined,
}) => {
	const { id, tab, underwritingTab } = useParams();
	const [editing, setEditing] = useState(false);
	const editField = useCallback(() => setEditing(!editing), [editing]);

	const dispatch = useDispatch();

	const queryClient = useQueryClient();

	const [isBusinessAddressLocation, setIsBusinessAddressLocation] =
		useState(false);
	const [isLegalAddressLocation, setIsLegalAddressLocation] = useState(false);

	const { data: sponsors, isFetching: isLoadingSponsors } = useSponsorsQuery({
		applicationId: id,
		pageName: "Underwriting",
		options: {
			enabled: isSponsorEditable,
			select: sponsorsList =>
				sponsorsList?.filter(
					sponsor =>
						!["Wells Retail", "US Bank"].includes(sponsor.name),
				),
		},
	});

	const { data: processors, isFetching: isLoadingProcessors } =
		useProcessorsQuery({
			applicationId: id,
			pageName: "Underwriting",
			options: {
				enabled: isProcessorEditable,
				select: processorsList =>
					processorsList?.filter(
						processor =>
							![
								"Fiserv North",
								"Fiserv Omaha Retail",
								"Elavon",
							].includes(processor.name),
					),
			},
		});

	useEffect(() => {
		setIsBusinessAddressLocation(
			merchantData?.businessAddress?.latitude &&
				merchantData?.businessAddress?.longitude,
		);
		setIsLegalAddressLocation(
			merchantData?.legalAddress?.latitude &&
				merchantData?.legalAddress?.longitude,
		);
	}, [merchantData]);

	const BlockWithLoader = useCallback(
		({ component }) => {
			if (isLoading) {
				return (
					<Skeleton
						width={window.innerWidth < 1250 ? "100%" : "25%"}
						minHeight="428px"
						marginLeft={window.innerWidth < 1250 ? 0 : "30px"}
						marginTop={window.innerWidth < 1250 ? "30px" : 0}
					/>
				);
			} else {
				return component;
			}
		},
		[isLoading],
	);

	const showViewFullAppDocument = () => {
		checkFullAppDocument({
			id: merchantData?.applicationId,
			errorLink: `/underwriting/${tab}/${id}/${underwritingTab}/error`,
		});
	};

	const showOwner = owner => {
		dispatch(
			uiActions.showModal("MERCHANT", {
				owner,
				handleShowViewFullAppDocument: showViewFullAppDocument,
			}),
		);
	};

	const businessAddress = getAddress(merchantData?.businessAddress);
	const legalAddress = getAddress(merchantData?.legalAddress);

	const cardAcceptanceMethod = useMemo(() => {
		const swiped = merchantData?.swiped
			? `${merchantData?.swiped}%`
			: " - ";
		const moTo = merchantData?.keyed ? `${merchantData?.keyed}%` : " - ";
		const eCommerce = merchantData?.eCommerce
			? `${merchantData?.eCommerce}%`
			: " - ";
		return `Swiped: ${swiped}, MO/TO: ${moTo}, eCommerce:${eCommerce}`;
	}, [merchantData?.eCommerce, merchantData?.keyed, merchantData?.swiped]);
	const acceptedCardsPreviously = useMemo(() => {
		return merchantData?.haveYouAcceptedCardsPreviosly ? "Yes" : "No";
	}, [merchantData?.haveYouAcceptedCardsPreviosly]);
	const sellingOutsideOfUS = useMemo(() => {
		return merchantData?.sellingOutsideOfUS ? "Yes" : "No";
	}, [merchantData?.sellingOutsideOfUS]);
	const pageLocation = useLocation();
	const editApplicationUrl = useMemo(() => {
		return `/applications/edit/${merchantData?.applicationId}/get-started?from=underwriting&return=${pageLocation?.pathname}`;
	}, [merchantData?.applicationId, pageLocation?.pathname]);

	const { mutate: submitMCCField, isLoading: isLoadingMCCField } =
		useMCCFieldMutation();

	const { mutate: submitTaxIdField, isLoading: isLoadingTaxIdField } =
		useTaxIdFieldMutation();

	const { mutate: submitProcessorField, isLoading: isLoadingProcessorField } =
		useProcessorFieldMutation();

	const { mutate: submitSponsorField, isLoading: isLoadingSponsorField } =
		useSponsorFieldMutation();

	const { mutate: checkFullAppDocument } = useCheckIfDocumentExistsMutation();

	const handleMerchantDescriptionField = useCallback(
		values => {
			if (
				merchantData?.merchantDescription !== values.merchantDescription
			) {
				return submitMCCField?.({
					id,
					values,
					onSuccessHandler: editField(),
				});
			} else {
				editField();
			}
		},
		[editField, id, merchantData?.merchantDescription, submitMCCField],
	);

	const handleTaxIdField = useCallback(
		(values, hideEditField) => {
			if (merchantData?.taxId !== values.taxId) {
				return submitTaxIdField?.({
					id,
					values,
					onSuccessHandler: () => {
						hideEditField();
						getMerchantInformation(id, tab);
					},
				});
			} else {
				hideEditField();
			}
		},
		[
			getMerchantInformation,
			id,
			merchantData?.taxId,
			submitTaxIdField,
			tab,
		],
	);

	const handleProcessorField = useCallback(
		(values, hideEditField) => {
			if (
				merchantData?.processor?.id !== parseInt(values.processor?.id)
			) {
				return submitProcessorField?.({
					id,
					values,
					onSuccessHandler: () => {
						hideEditField();
						getMerchantInformation(id, tab);
					},
				});
			} else {
				hideEditField();
			}
		},
		[
			getMerchantInformation,
			id,
			merchantData?.processor?.id,
			submitProcessorField,
			tab,
		],
	);

	const handleSponsorField = useCallback(
		(values, hideEditField) => {
			if (merchantData?.sponsor?.id !== parseInt(values.sponsor?.id)) {
				let filterAllSponsorData = sponsors.filter(value => {
					// eslint-disable-next-line
					return value.id == values.sponsor;
				});
				let sponsorResult = Object.assign({}, ...filterAllSponsorData);
				return submitSponsorField?.({
					id,
					values: sponsorResult,
					onSuccessHandler: () => {
						queryClient.resetQueries(["getApplicationValues"]);
						refetchBankNotes?.();
						hideEditField();
						getMerchantInformation(id, tab);
					},
				});
			} else {
				hideEditField();
			}
		},
		[
			refetchBankNotes,
			getMerchantInformation,
			id,
			merchantData?.sponsor?.id,
			queryClient,
			sponsors,
			submitSponsorField,
			tab,
		],
	);

	return (
		<div className={styles.wrappMainMerchant}>
			<div className={styles.wrappMerchantInfo}>
				<div className={styles.merchantContent}>
					<div className={styles.merchantForm}>
						<div className={styles.merchantLeft}>
							<FieldRow
								label="Doing Business As Name (DBA)"
								data={merchantData?.businessName}
								isLoading={isLoading}
								className="overflow-break-word"
							/>

							<FieldRow
								label="Legal Business Name"
								data={merchantData?.legalName}
								isLoading={isLoading}
								className="overflow-break-word"
							/>
							<AddressFieldRow
								label="DBA Address"
								data={merchantData?.businessAddress}
								value={businessAddress}
								isLoading={isLoading}
								isDisabled={isBusinessAddressLocation}
							/>

							{!merchantData?.isLegalAddressTheSame && (
								<AddressFieldRow
									label="Legal Address"
									data={merchantData?.legalAddress}
									value={legalAddress}
									isLoading={isLoading}
									isDisabled={isLegalAddressLocation}
								/>
							)}

							<FieldRow
								label="Business Phone"
								data={formatPhoneNumber(
									merchantData?.phoneNumber,
								)}
								isLoading={isLoading}
							/>

							<MerchantDescriptionForm
								onSubmit={handleMerchantDescriptionField}
								editField={editField}
								editing={editing}
								isEditable={isEditable}
								isLoading={isLoadingMCCField || isLoading}
							/>

							<FieldRow
								label="Detailed explanation of Product(s) or
									Service(s) sold"
								data={
									merchantData?.detailedExplanationWhatDoYouSell ||
									"Brief description of Product(s) or Service(s) sold are not provided"
								}
								isLoading={isLoading}
							/>

							<FieldRow
								label="Type of Business"
								data={TypeOfBusinessValues.getDisplayName(
									merchantData?.typeOfBusiness,
								)}
								isLoading={isLoading}
							/>

							<TaxIdField
								onSubmit={handleTaxIdField}
								isEditable={isEditable}
								label="Federal Tax ID"
								nameField="taxId"
								initialValue={taxIdNormalizer(
									merchantData?.taxId,
								)}
								isLoading={isLoadingTaxIdField || isLoading}
							/>

							<FieldRow
								label="Business Est Date"
								data={
									merchantData?.businessEstablishedDate
										? moment(
												merchantData?.businessEstablishedDate,
										  ).format("MM/DD/YYYY")
										: ""
								}
								isLoading={isLoading}
							/>
						</div>

						<div className={styles.merchantRight}>
							<FieldRow
								label="Card Acceptance Method"
								data={cardAcceptanceMethod}
								isLoading={isLoading}
							/>

							{merchantData?.eCommerce > 0 && (
								<>
									<FieldRow
										label="Refund Policy"
										data={RefundPolicyByValue(
											merchantData?.refundPolicy,
										)}
										isLoading={isLoading}
									/>
									{RefundPolicyByValue(
										merchantData?.refundPolicy,
									) === "Other" && (
										<FieldRow
											label="&nbsp;"
											data={
												merchantData?.refundPolicyIfOther
											}
											isLoading={isLoading}
										/>
									)}
								</>
							)}

							<FieldRow
								label="Accepted card previously?"
								data={acceptedCardsPreviously}
								isLoading={isLoading}
							/>
							<FieldRow
								label="Previous Processor"
								data={merchantData?.whoWasPreviousProcessor}
								isLoading={isLoading}
							/>
							{merchantData?.eCommerce > 0 && (
								<>
									<FieldRow
										label="Credit & Debit Card Sales Transactions are Settled"
										data={CreditDebitByValueTypeCart(
											merchantData?.creditAndDebitCardTransactionsAreSettled,
										)}
										isLoading={isLoading}
									/>
									{CreditDebitByValueTypeCart(
										merchantData?.creditAndDebitCardTransactionsAreSettled,
									) === "Other" && (
										<FieldRow
											label="&nbsp;"
											data={
												merchantData?.creditDebitCardTransactionsAreSettledTypeIfOther
											}
											isLoading={isLoading}
										/>
									)}

									<FieldRow
										label="Selling Outside of US?"
										data={sellingOutsideOfUS}
										isLoading={isLoading}
									/>
								</>
							)}

							<FieldRow
								label="Website"
								data={merchantData?.url}
								isLoading={isLoading}
							/>
							<EditInformationField
								label="Processor"
								nameField="processor"
								dropdownItems={processors}
								initialValue={{
									id: merchantData?.processor?.id,
									name: merchantData?.processor?.name,
								}}
								isLoading={
									isLoadingProcessorField ||
									isLoading ||
									isLoadingProcessors
								}
								onSubmit={handleProcessorField}
								isEditable={isProcessorEditable}
							/>
							<EditInformationField
								label="Sponsor"
								nameField="sponsor"
								dropdownItems={sponsors}
								initialValue={{
									id: merchantData?.sponsor?.id,
									name: merchantData?.sponsor?.name,
								}}
								isLoading={
									isLoadingSponsorField ||
									isLoading ||
									isLoadingSponsors
								}
								onSubmit={handleSponsorField}
								isEditable={isSponsorEditable}
							/>
							{merchantData?.ach_processor && (
								<FieldRow
									label={
										ACHProcessorData.ACHProcessor
											.displayName
									}
									data={
										AchProcessorTypes.getByName(
											merchantData?.ach_processor,
										)?.displayName
									}
									isLoading={isLoading}
								/>
							)}
						</div>
					</div>
					<div className={styles.wrappBtn}>
						<HasPermissions
							requiredPermissions={viewSensitiveDataPermissionSet}
						>
							<Button
								disabled={isLoading}
								onClick={showViewFullAppDocument}
								value="View Full App"
							/>
						</HasPermissions>
						{isEditable && (
							<Button
								value="Edit Application"
								as={OnlineLink}
								to={editApplicationUrl}
								className={clsx(
									isLoading && "disabled",
									"ml-4",
								)}
							/>
						)}
					</div>
				</div>
				<BlockWithLoader
					component={
						<div className={styles.merchantOwners}>
							{merchantData?.owner1 !== null ? (
								<div className={styles.wrappMerchantPerson}>
									<h3>Owner 1</h3>
									<div className={styles.ownerPerson}>
										<div className="d-flex align-items-center">
											<p className={styles.name}>
												{getFullName(
													merchantData?.owner1,
												)}
											</p>
										</div>
										<img
											src={ShowIcon}
											alt=""
											className={styles.showIcon}
											onClick={() =>
												showOwner(merchantData?.owner1)
											}
										/>
									</div>
								</div>
							) : null}
							{merchantData?.owner2 !== null ? (
								<div className={styles.wrappMerchantPerson}>
									<h3>Owner 2</h3>
									<div className={styles.ownerPerson}>
										<div className="d-flex align-items-center">
											<p className={styles.name}>
												{getFullName(
													merchantData?.owner2,
												)}
											</p>
										</div>
										<img
											src={ShowIcon}
											alt=""
											className={styles.showIcon}
											onClick={() =>
												showOwner(merchantData?.owner2)
											}
										/>
									</div>
								</div>
							) : null}
							{merchantData?.owner3 !== null && (
								<div className={styles.wrappMerchantPerson}>
									<h3>Owner 3</h3>
									<div className={styles.ownerPerson}>
										<div className="d-flex align-items-center">
											<p className={styles.name}>
												{getFullName(
													merchantData?.owner3,
												)}
											</p>
										</div>
										<img
											src={ShowIcon}
											alt=""
											className={styles.showIcon}
											onClick={() =>
												showOwner(merchantData?.owner3)
											}
										/>
									</div>
								</div>
							)}
							{merchantData?.owner4 !== null && (
								<div className={styles.wrappMerchantPerson}>
									<h3>Owner 4</h3>
									<div className={styles.ownerPerson}>
										<div className="d-flex align-items-center">
											<p className={styles.name}>
												{getFullName(
													merchantData?.owner4,
												)}
											</p>
										</div>
										<img
											src={ShowIcon}
											alt=""
											className={styles.showIcon}
											onClick={() =>
												showOwner(merchantData?.owner4)
											}
										/>
									</div>
								</div>
							)}
							{merchantData?.authorizedSigner !== null && (
								<div className={styles.wrappMerchantPerson}>
									<h3>Authorized Signer</h3>
									<div className={styles.ownerPerson}>
										<div className="d-flex align-items-center">
											<p className={styles.name}>
												{getFullName(
													merchantData?.authorizedSigner,
												)}
											</p>
										</div>
										<img
											src={ShowIcon}
											alt=""
											className={styles.showIcon}
											onClick={() =>
												showOwner(
													merchantData?.authorizedSigner,
												)
											}
										/>
									</div>
								</div>
							)}
						</div>
					}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = state => ({
	isLoading: state.merchantInfo.loading,
	merchantData: state.merchantInfo.merchantInfo,
});

const mapDispatchToProps = {
	getMerchantInformation: merchantInfoActions.getMerchantInformation,
};

export default connect(mapStateToProps, mapDispatchToProps)(MerchantInfo);
